<script setup lang="ts">
import type { StoredAccount } from "@verbleif/shared/src/composables/AccountsStore/useAccountsStore";
import VButton from "@verbleif/lib/src/components/Button/VButton.vue";
import { closeReserveClass } from "@verbleif/lib/src/components/Dialog/dialogSizes";
import VIcon from "@verbleif/lib/src/components/Icon/VIcon.vue";
import VInput from "@verbleif/lib/src/components/Input/VInput.vue";
import VTag from "@verbleif/lib/src/components/Tag/VTag.vue";
import { useDialogLabel } from "@verbleif/lib/src/composables/Dialog/useDialogLabel";
import { tryUseDialogLayer } from "@verbleif/lib/src/composables/Dialog/useDialogLayer";
import Avatar from "@verbleif/shared/src/components/Avatar/Avatar.vue";
import { releaseAccountSlot } from "@verbleif/shared/src/composables/AccountsStore/releaseAccountSlot";
import { useAccountsStore } from "@verbleif/shared/src/composables/AccountsStore/useAccountsStore";
import { useLogout } from "@web/features/Auth/Logout/useLogout";
import { computed, ref } from "vue";
import { useI18n } from "vue-i18n";
import { accountContextLine } from "./accountContext";
import { confirmLogoutAll } from "./confirmLogoutAll";

/**
 * "Je accounts": every account signed in on this browser with search, switching, signing in again for expired
 * sessions and removing an account from the list. It is the overflow of the account menu, which lists three.
 */
const { t } = useI18n();
const TRANSLATION_KEY = "navbar.dropdown";
const accountsStore = useAccountsStore();
const { switchAccount, reloginAccount, addAccount, logout } = useLogout();
const layer = tryUseDialogLayer();

const titleId = useDialogLabel("title");
const headerReserveClass = closeReserveClass(true);

const search = ref("");
const removing = ref<number | null>(null);

const allAccounts = computed<StoredAccount[]>(() => {
  const active = accountsStore.activeAccount.value;
  return active ? [active, ...accountsStore.otherAccounts.value] : accountsStore.otherAccounts.value;
});

const visibleAccounts = computed(() => {
  const needle = search.value.trim().toLowerCase();
  if (needle === "") {
    return allAccounts.value;
  }
  return allAccounts.value.filter(account => [account.name, account.username, account.locationName, account.clientName]
    .some(field => (field ?? "").toLowerCase().includes(needle)));
});

function isActive(account: StoredAccount): boolean {
  return account.index === accountsStore.activeIndex;
}

async function removeFromList(account: StoredAccount) {
  removing.value = account.index;
  try {
    // The slot token is needed to end the session, so it goes before the slot does.
    await releaseAccountSlot(account.index);
    accountsStore.removeAccount(account.index);
  } finally {
    removing.value = null;
  }
}

async function logoutAll() {
  if (!await confirmLogoutAll()) {
    return;
  }
  await layer?.closeAfterSave();
  await logout({ allAccounts: true });
}
</script>

<template>
  <div class="flex w-full flex-col" data-testid="accounts-dialog">
    <header class="border-b border-gray-100 py-3.5 pl-5 dark:border-dark-700" :class="headerReserveClass">
      <h2 :id="titleId" class="m-0 text-base leading-5 font-semibold text-(--text)">
        {{ t(`${TRANSLATION_KEY}.accounts_dialog_title`, { count: allAccounts.length }) }}
      </h2>
    </header>

    <div class="flex flex-col gap-3 px-5 pt-4 pb-4">
      <VInput
        v-model="search"
        type="search"
        variant="is-white"
        name="accounts-search"
        icon-left="search"
        :placeholder="t(`${TRANSLATION_KEY}.accounts_search`)"
        :aria-label="t(`${TRANSLATION_KEY}.accounts_search`)"
        autocomplete="off"
        clearable
      />
      <ul class="m-0 flex max-h-80 list-none flex-col gap-1 overflow-y-auto p-0" data-testid="accounts-dialog-list">
        <li
          v-for="account in visibleAccounts"
          :key="account.index"
          class="flex items-center gap-3 rounded-xl px-2 py-2"
          data-testid="accounts-dialog-row"
        >
          <Avatar :name="account.name" :avatar-url="account.avatarUrl" size="sm" />
          <div class="flex min-w-0 flex-1 flex-col">
            <span class="truncate text-sm leading-5 font-medium text-gray-900 dark:text-gray-100">{{ account.name }}</span>
            <span class="truncate text-xs leading-4 text-gray-500 dark:text-gray-400">{{ account.username }}</span>
            <span v-if="accountContextLine(account)" class="truncate text-xs leading-4 text-gray-500 dark:text-gray-400">{{ accountContextLine(account) }}</span>
          </div>
          <VTag v-if="isActive(account)" variant="is-success" :text="t(`${TRANSLATION_KEY}.active_tag`)" icon-less />
          <template v-else>
            <VButton
              v-if="account.expired"
              size="small"
              variant="warning"
              rounded
              data-testid="accounts-dialog-relogin"
              @click="reloginAccount(account.index)"
            >
              {{ t(`${TRANSLATION_KEY}.relogin`) }}
            </VButton>
            <VButton
              v-else
              size="small"
              variant="gray"
              rounded
              data-testid="accounts-dialog-switch"
              @click="switchAccount(account.index)"
            >
              {{ t(`${TRANSLATION_KEY}.switch`) }}
            </VButton>
            <button
              type="button"
              class="flex size-8 shrink-0 cursor-pointer items-center justify-center rounded-full text-gray-500 hover:bg-gray-100 focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:outline-hidden disabled:cursor-wait disabled:opacity-50 dark:text-gray-400 dark:hover:bg-dark-600"
              :aria-label="t(`${TRANSLATION_KEY}.remove_account`, { name: account.name })"
              :disabled="removing === account.index"
              data-testid="accounts-dialog-remove"
              @click="removeFromList(account)"
            >
              <VIcon name="x" aria-hidden="true" />
            </button>
          </template>
        </li>
        <li v-if="visibleAccounts.length === 0" class="px-2 py-4 text-center text-sm text-gray-500 dark:text-gray-400" data-testid="accounts-dialog-empty">
          {{ t(`${TRANSLATION_KEY}.accounts_none_found`) }}
        </li>
      </ul>
    </div>

    <footer class="flex flex-wrap items-center justify-between gap-2 border-t border-gray-100 bg-gray-50/60 px-4 py-3 dark:border-dark-700 dark:bg-dark-800/60">
      <VButton variant="danger" outlined rounded data-testid="accounts-dialog-logout-all" @click="logoutAll">
        {{ t(`${TRANSLATION_KEY}.logout_all_accounts`) }}
      </VButton>
      <VButton rounded icon-left="plus" data-testid="accounts-dialog-add" @click="addAccount()">
        {{ t(`${TRANSLATION_KEY}.add_account`) }}
      </VButton>
    </footer>
  </div>
</template>
